/**
 * Stream-health "lag" model for the fan viewer (handover §3).
 *
 * One severity scale drives every lag surface — car-list thumbs, map
 * pins, and the Driver-tab telemetry cell:
 *
 *   fresh      < 2 s   green, pulsing dot
 *   lagging    2–60 s  amber
 *   buffering  player mounted but no PROGRAM-DATE-TIME yet — cyan, pulsing
 *   stale      ≥ 60 s  red
 *   offline    armed / idle / replay — muted
 *
 * Two DATA SOURCES feed the scale (each call site comments which):
 *   1. Active player: newest GPS fix − video end (the newest playable
 *      media's PDT, surfaced by HLSPlayer's `onVideoEdgeUpdate`) —
 *      how far the delivered video trails the car's live position.
 *      Playhead-independent: scrubbing back is not lag. Both stamps
 *      are phone-clock, so client skew cancels.
 *   2. Everything else: last-telemetry age (device SSE freshness,
 *      `fix_ts_ms` vs now) as the MVP proxy — Pos/Gap-grade data, not
 *      playback lag, but it colors identically so the language is one.
 */

import { useEffect, useState } from 'react';

export type LagSeverity = 'fresh' | 'lagging' | 'buffering' | 'stale' | 'offline';

export interface LagInfo {
  severity: LagSeverity;
  /** Short chip label, e.g. "1.2s", "6s", "2m", "buffer", "armed". */
  label: string;
}

const FRESH_MAX_S = 2;
const STALE_MIN_S = 60;

/** Format a lag/age in seconds for the chip label. */
export function formatLagSeconds(sec: number): string {
  if (sec >= STALE_MIN_S) return `${Math.floor(sec / 60)}m`;
  if (sec < 10) return `${Math.max(0, sec).toFixed(1)}s`;
  return `${Math.round(sec)}s`;
}

/**
 * Severity from the ACTIVE PLAYER's lag (source 1: newest GPS fix −
 * video end, derived in BrowseStateView from HLSPlayer.onVideoEdgeUpdate).
 * `null` = player mounted but no PDT known yet → buffering.
 */
export function lagFromPlayer(lagSec: number | null): LagInfo {
  if (lagSec == null) return { severity: 'buffering', label: 'buffer' };
  if (lagSec < FRESH_MAX_S) return { severity: 'fresh', label: formatLagSeconds(lagSec) };
  if (lagSec < STALE_MIN_S) return { severity: 'lagging', label: formatLagSeconds(lagSec) };
  return { severity: 'stale', label: formatLagSeconds(lagSec) };
}

/**
 * Severity from DEVICE TELEMETRY freshness (source 2: SSE `fix_ts_ms`
 * age — the MVP proxy for cameras we aren't actively playing).
 */
export function lagFromTelemetryAge(
  fixTsMs: number | null | undefined,
  nowMs: number,
): LagInfo {
  if (fixTsMs == null) return { severity: 'offline', label: 'no fix' };
  const ageSec = Math.max(0, (nowMs - fixTsMs) / 1000);
  if (ageSec < FRESH_MAX_S) return { severity: 'fresh', label: formatLagSeconds(ageSec) };
  if (ageSec < STALE_MIN_S) return { severity: 'lagging', label: formatLagSeconds(ageSec) };
  return { severity: 'stale', label: formatLagSeconds(ageSec) };
}

/** Tailwind text color per severity (telemetry cells, chip text).
 *  'lagging' is the dedicated yellow token, NOT the amber brand accent
 *  — amber means selected/active everywhere else in the viewer, so an
 *  amber severity read as "featured" instead of "behind". */
export const SEVERITY_TEXT: Record<LagSeverity, string> = {
  fresh: 'text-status-online',
  lagging: 'text-status-lagging',
  buffering: 'text-accent-cyan',
  stale: 'text-status-error',
  offline: 'text-fg-muted',
};

/** Tailwind dot (background) color per severity. */
export const SEVERITY_DOT: Record<LagSeverity, string> = {
  fresh: 'bg-status-online',
  lagging: 'bg-status-lagging',
  buffering: 'bg-accent-cyan',
  stale: 'bg-status-error',
  offline: 'bg-fg-muted',
};

/** Pulsing severities (fresh + buffering animate, per the mockup). */
export const SEVERITY_PULSES: Record<LagSeverity, boolean> = {
  fresh: true,
  lagging: false,
  buffering: true,
  stale: false,
  offline: false,
};

/** Raw CSS color (design token var) per severity — for map divIcons
 *  where Tailwind classes can't reach into Leaflet HTML strings. */
export const SEVERITY_CSS_COLOR: Record<LagSeverity, string> = {
  fresh: 'var(--status-online)',
  lagging: 'var(--status-lagging)',
  buffering: 'var(--accent-cyan)',
  stale: 'var(--status-error)',
  offline: 'var(--text-muted)',
};

/**
 * 1 Hz wall-clock tick. Telemetry-age chips re-derive from this so a
 * pin that stops receiving SSE frames visibly ages fresh → lagging →
 * stale without any new data arriving.
 */
export function useNowTick(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}
